import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import Card from "@mui/material/Card";
import { useSelector, useDispatch } from "react-redux";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import axios from "axios";
import { updateaccount, removeaccount } from "config";
import { useNavigate } from "react-router-dom";
import { AccountsContext } from "context/AccountsContext";
import { setSelectedStudent } from "features/StudentData/studentSlice";

export default function AccountDetailsForm() {
  const { handleAccount, accounts, loading: accountsLoading } = useContext(AccountsContext);
  const dispatch = useDispatch();
  const selectedStudent = useSelector((state) => state.student.selectedStudent);
  const navigate = useNavigate();

  useEffect(() => {
    handleAccount();
  }, []);

  const accountOptions = useMemo(() => {
    return Object.values(accounts || {})
      .map((item) => item.account)
      .filter(Boolean)
      .map((acc) => ({
        ...acc,
        fullName: [acc.firstName, acc.lastName].filter(Boolean).join(" ").trim() || "-",
        admissionNo: acc.accountId?.split("::")[1] || acc.accountId || "-",
      }));
  }, [accounts]);

  const handleSelectAccount = (acc) => {
    dispatch(setSelectedStudent(acc ? { account: acc } : null));
  };

  const [formData, setFormData] = useState({
    accountId: "",
    firstName: "",
    lastName: "",
    rollNumber: "",
    standard: "",
    guardianNumbers: [],
    rfidTag: "",
    address: "",
    role: "STUDENT",
  });

  const matchedAccount = useMemo(
    () => accountOptions.find((o) => o.accountId === formData.accountId) || null,
    [accountOptions, formData.accountId]
  );
  const isEditMode = !!matchedAccount;

  const handleNewAccountId = (typedValue) => {
    if (selectedStudent) {
      dispatch(setSelectedStudent(null));
    }
    setFormData((prev) => ({ ...prev, accountId: typedValue }));
  };

  useEffect(() => {
    if (selectedStudent) {
      setFormData({
        accountId: selectedStudent.account?.accountId || "",
        firstName: selectedStudent.account?.firstName || "",
        lastName: selectedStudent.account?.lastName || "",
        rollNumber: selectedStudent.account?.extra?.rollNumber || "",
        standard: selectedStudent.account?.extra?.standard || "",
        guardianNumbers: selectedStudent.account?.extra?.guardians || [],
        rfidTag: selectedStudent.account?.extra?.rfidTag || "",
        address: selectedStudent.account?.address || "",
        role: selectedStudent.account?.role || "STUDENT",
      });
    }
    // 👈 Note: intentionally no "else" reset here anymore. When
    // selectedStudent becomes null (e.g. user typing a new Employee Id via
    // handleNewAccountId, or handleClear), formData is already managed
    // explicitly by that caller — resetting it here would wipe out the
    // Employee Id the user just typed.
  }, [selectedStudent]);

  
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const [newGuardian, setNewGuardian] = useState("");

  const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);


  // guardian add
const handleAddGuardian = () => {
  if (
    newGuardian.trim() !== "" &&
    isValidEmail(newGuardian)
  ) {
    setFormData({
      ...formData,
      guardianNumbers: [
        ...formData.guardianNumbers,
        newGuardian.toLowerCase(), // optional
      ],
    });
    setNewGuardian("");
  }
};

  
  // guardian remove
  const handleRemoveGuardian = (index) => {
    const updated = [...formData.guardianNumbers];
    updated.splice(index, 1);
    setFormData({ ...formData, guardianNumbers: updated });
  };
  
  const handleClear = () => {
    setFormData({
      accountId: "",
      firstName: "",
      lastName: "",
      rollNumber: "",
      standard: "",
      guardianNumbers: [],
      rfidTag: "",
      address: "",
      role: "STUDENT",
    });
    dispatch(setSelectedStudent(null));
  };

  const handleUploadExcel = () => {
    console.log("Upload Excel clicked");
    navigate("/bulk-student-upload");   
    openSuccessSB();
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        accounts: [
          {
            accountId: isEditMode
              ? formData.accountId?.split("::")[1]
              : formData.accountId,
          
            firstName: formData.firstName,
            lastName: formData.lastName,
            role: formData.role,
            address: formData.address,
            rfidTag: formData.rfidTag || null,
            extra: {
              standard: formData.standard,
              rollNumber: formData.rollNumber,
              guardians: formData.guardianNumbers || [],
            },
          },
        ],
      };
      console.log(payload)
      const token = localStorage.getItem("token");

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: updateaccount,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
      };

      const response = await axios.request(config);
      console.log("✅ API Response:", response.data);
      handleAccount();
      setFormData({
        accountId: "",
        firstName: "",
        lastName: "",
        rollNumber: "",
        standard: "",
        guardianNumbers: [],
        rfidTag: "",
        address: "",
        role: "STUDENT",
      });
      openSuccessSB();
    } catch (error) {
      console.error("❌ API Error:", error);
      openErrorSB();
    }
  };

  const handleRemove = async (accountId) => {
    try {
      const payload = [accountId];
      const token = localStorage.getItem("token");
      const config = {
        method: "post",
        url: removeaccount,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
        maxBodyLength: Infinity,
      };

      const response = await axios.request(config);
      console.log("✅ Remove Response:", response.data);
      handleAccount();
      setFormData({
        accountId: "",
        firstName: "",
        lastName: "",
        rollNumber: "",
        standard: "",
        guardianNumbers: [],
        rfidTag: "",
        address: "",
        role: "STUDENT",
      });
      openSuccessSB();
    } catch (error) {
      console.error("❌ Remove API Error:", error);
      openErrorSB();
    }
  };


  const getTextFieldHeight = () => {
    if (isMobile) return "48px";
    if (isTablet) return "52px";
    return "56px";
  };
 
  const getButtonStyles = () => ({
    fontSize: isMobile ? "0.75rem" : isTablet ? "0.875rem" : "1rem",
    padding: isMobile ? "5px 10px" : isTablet ? "7px 14px" : "9px 18px",
    minWidth: isMobile ? "100px" : isTablet ? "120px" : "140px",
    // bgcolor:"#1398DC",
    // color:"#fff"
  });

  const getCardPadding = () => {
    if (isMobile) return 2;
    if (isTablet) return 3;
    return 4;
  };

  const getMDBoxPadding = () => {
    if (isMobile) return 3;
    if (isTablet) return 4;
    return 5;
  };

  // Snackbars
  const renderSuccessSB = (
    <MDSnackbar
      color="success"
      icon="check"
      title="Account Action"
      content={isEditMode ? "Account updated successfully!" : "Account added successfully!"}
      dateTime="Just now"
      open={successSB}
      onClose={closeSuccessSB}
      close={closeSuccessSB}
      bgWhite
    />
  );

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title="Account Action"
      content={isEditMode ? "Failed to update account. Please try again." : "Failed to add account. Please try again."}
      dateTime="Just now"
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
      bgWhite
    />
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ maxWidth: 900, mx: "auto", p: getCardPadding() }}>
              {/* Top Right Action Button */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleUploadExcel}
                  sx={getButtonStyles()}
                >
                  Upload Excel
                </MDButton>
              </Box>

              {/* Form */}
              <MDBox pt={getMDBoxPadding()} px={getMDBoxPadding()} pb={getMDBoxPadding()}>
                <Grid container spacing={2}>

                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      options={accountOptions}
                      loading={accountsLoading}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.fullName || option.accountId || ""
                      }
                      isOptionEqualToValue={(option, value) => {
                        const valId = typeof value === "string" ? value : value?.accountId;
                        return option.accountId === valId;
                      }}
                      value={matchedAccount || formData.accountId || null}
                      inputValue={formData.accountId || ""}
                      onChange={(event, newValue) => {
                        if (newValue && typeof newValue === "object") {
                          // ✅ Existing account dropdown se select kiya
                          handleSelectAccount(newValue);
                        } else if (typeof newValue === "string") {
                          // ✅ Naya Employee Id type karke Enter dabaya / free text select kiya
                          handleNewAccountId(newValue);
                        } else {
                          handleSelectAccount(null);
                        }
                      }}
                      onInputChange={(event, newInputValue, reason) => {
                        // Har keystroke par accountId field ko sync me rakho —
                        // isse dropdown se select kiye bina bhi naya Employee Id
                        // seedha type karke new entry banayi ja sakti hai.
                        if (reason === "input") {
                          handleNewAccountId(newInputValue);
                        }
                      }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.accountId}
                          style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                        >
                          <span>{option.fullName}</span>
                          <span style={{ color: "gray", fontSize: "0.85rem" }}>{option.admissionNo}</span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Employee Id"
                          placeholder="Select an existing Employee Id or type a new one"
                          required
                          fullWidth
                        />
                      )}
                    />
                  </Grid>


                  {/* First + Last Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* Roll Number + Standard */}
                  {/* <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Roll Number"
                      name="rollNumber"
                      value={formData.rollNumber}
                      onChange={handleChange}
                    />
                  </Grid> */}

                  {/* <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Standard"
                      name="standard"
                      value={formData.standard}
                      onChange={handleChange}
                    />
                  </Grid> */}
       
                  <Grid item xs={12}>
  <Box display="flex" gap={2} mb={2}>
    {/* <TextField
      fullWidth
      label="Guardian Number"
      value={newGuardian}
      onChange={(e) => setNewGuardian(e.target.value)}
      placeholder="Enter 10 digit number"
    /> */}
    <TextField
  fullWidth
  label="Crew Email"
  value={newGuardian}
  onChange={(e) => setNewGuardian(e.target.value)}
  placeholder="Enter email address"
/>

    <MDButton
      variant="gradient"
      color="info"
      onClick={handleAddGuardian}
    >
      Add
    </MDButton>
  </Box>

  {/* Show added guardians */}
  <Box display="flex" flexWrap="wrap" gap={1}>
    {formData.guardianNumbers.map((num, idx) => (
      <Box
        key={idx}
        sx={{
          display: "flex",
          alignItems: "center",
          background: "#f0f0f0",
          borderRadius: "20px",
          padding: "3px 6px",
        }}
      >
        <span>{num}</span>
        <MDButton
          onClick={() => handleRemoveGuardian(idx)}
          sx={{ ml: 1, minWidth: "20px", padding: "2px 4px" }}
          color="error"
        >
          X
        </MDButton>
      </Box>
    ))}
  </Box>
</Grid>


                  {/* <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={formData.guardianNumbers || []}
                      onChange={(event, newValue) => {
                        setFormData({ ...formData, guardianNumbers: newValue });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Guardian Numbers"
                          placeholder="Enter guardian numbers"
                          style={{ marginBottom: 16 }}
                        />
                      )}
                    />
                  </Grid> */}

                  {/* <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="RFID Tag"
                      name="rfidTag"
                      value={formData.rfidTag}
                      onChange={handleChange}
                    />
                  </Grid> */}

                  {/* Address */}
                  {/* <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                    />
                  </Grid> */}

                  {/* Role */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      sx={{
                        "& .MuiInputBase-root": { height: getTextFieldHeight() },
                        "& .MuiInputBase-input": {
                          padding: isMobile
                            ? "12px 14px"
                            : isTablet
                              ? "14px 14px"
                              : "16px 14px",
                        },
                        "& .MuiMenuItem-root": {
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          padding: isMobile ? "8px 16px" : "12px 16px",
                        },
                      }}
                    >
                      <MenuItem value="STUDENT">PILOT</MenuItem>
                      <MenuItem value="STAFF">STAFF</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                {/* Buttons */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSubmit}
                    sx={getButtonStyles()}
                  >
                    {isEditMode ? "Update Account" : "Add Account"}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleClear}
                    sx={getButtonStyles()}
                  >
                    Clear
                  </MDButton>
                  {isEditMode && (
                    <MDButton
                      variant="gradient"
                      color="error"
                      onClick={() => handleRemove(formData.accountId)}
                      sx={getButtonStyles()}
                    >
                      Remove
                    </MDButton>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {renderSuccessSB}
      {renderErrorSB}
    </DashboardLayout>
  );
}